<template>
    <div class="edit-segment-page" ref="editSegmentPageContainer">
        <SystemMessage :message="errorMessage" :dismissButton="false" v1 v-if="!!errorMessage" />
        <SystemMessage :message="!segmentId ? ' ' : ''"
                       type="warning"
                       :dismissButton="false"
                       icon="notificationErrorIcon"
                       v1>
            Make sure to follow&nbsp;
            <a :href="links.segmentsSetup" class="link segments-guide-link" target="_blank">our dedicated</a>
            &nbsp;guide to setting up your segments
        </SystemMessage>
        <Loader :status="loading" />
        <div v-if="!loading" class="edit-segment-page-content-container">
            <AppHeader
                :platform="selectedApp.platform.toLowerCase()"
                :appIcon="selectedApp.icon"
                :appName="selectedApp.name"
                :appKey="selectedApp.appKey"
            />
            <div class="edit-segment-page-inner-wrapper">
                <SegmentConditions :conditions.sync="conditions"
                                   :errors="conditionsErrors"
                                   :metadata="metadata"
                                   :name.sync="$v.name.$model"
                                   :newSegment="!segmentId"
                                   :isAndroid="selectedApp.platform === 'Android'"
                                   :countriesData="countries"
                                   @touched="conditionTouched" />
                <SegmentExceptions :deliveryEnabled="deliveryEnabled"
                                   :exceptionsData.sync="exceptions"
                                   :placementsData="placements"
                                   :supportedAdUnits="supportedAdUnits"
                                   @toggleActive="toggleExceptionActive($event)"
                                   @editReward="editReward($event)"
                                   @editCapping="editCapping($event)"
                                   @addCapping="addCapping($event)" />
                <div class="edit-segment-page-footer-buttons">
                    <router-link id="editSegmentBackButton" class="font-5 link" :to="`/monetize/segments/${appKey}`">
                        <IconBase iconName="backV1Icon" width="18" height="13" />
                        Back to Segments
                    </router-link>
                    <Button id="saveSegmentButton" v1 large @click="saveSegment()">Save Segment</Button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import AppHeader from 'components/appHeader/AppHeader.vue';
import monetizeAppService from 'services/monetizeApp';
import Loader from 'components/loader/loader.vue';
import SystemMessage from 'components/SystemMessage/SystemMessage.vue';
import { mapActions, mapMutations } from 'vuex';
import generalConfig from 'services/metaData/generalConfig';
import SegmentConditions from '@/pages/monetize/segments/segmentForm/SegmentConditions.vue';
import SegmentExceptions from '@/pages/monetize/segments/segmentForm/SegmentExceptions.vue';
import appToSupportedAdUnits from '@/pages/monetize/segments/utils/supportedAdUnits';
import Button from 'components/button/Button.vue';
import IconBase from 'components/IconBase.vue';
import links from 'services/metaData/links';
import validators from 'services/libWrappers/vuelidate/validators';
import { getHttpErrorMessage } from 'services/metaData/errors';
import RewardModal from '@/pages/monetize/segments/modals/RewardModal.vue';
import CappingPacingModal from '@/pages/monetize/segments/modals/CappingPacingModal.vue';
import { customNameMaxLength, PROPERTY_IDS } from '@/pages/monetize/segments/segmentForm/consts';
import http from 'services/utils/http';
import { versionRegex } from 'services/utils/regex';
import { filterArchivedPlacements } from 'monetize/segments/utils/placements';
import { clearUndefinedFields } from 'services/utils/objectUtils';

const {
    required, maxLength, requiredIf
} = validators;

const segmentsApiPath = `${generalConfig().sspPlatformJsDomain}/api/v1/partners/segments`;
const segmentsApiKey = 'segments';
const placementsApiPath = '/api/rest/v1/placements?splitBy=deliveryGroup';
const placementsApiKey = 'placements';
const activatedProvidersForAppApiPath = '/partners/utils/activatedProvidersForApp?includeSupersonic=false&unit=all';
const activatedProvidersForAppApiKey = 'activatedProvidersForApp';

const minNumber = 1;
const maxNumber = 999999999.99;

export default {
    name: 'NewEditSegmentPage',
    props: {
        appKey: String,
        testGroup: {
            type: String,
            default: 'A'
        },
        segmentId: [Number, String]
    },
    data() {
        return {
            loading: true,
            showAllErrors: false,
            errorMessage: '',
            selectedApp: undefined,
            activatedProviders: undefined,
            countries: undefined,
            metadata: undefined,
            supportedAdUnits: undefined,
            placements: undefined,
            name: '',
            deliveryEnabled: { rewardedVideo: true, interstitial: true, banner: true },
            conditions: [],
            exceptions: { rewardedVideo: [], interstitial: [], banner: [] }
        };
    },
    components: {
        IconBase,
        Button,
        SegmentExceptions,
        SegmentConditions,
        AppHeader,
        Loader,
        SystemMessage
    },
    computed: {
        links: () => links,
        conditionsErrors() {
            const nameDirty = this.$v.name.$dirty;
            const conditionsDirty = this.$v.conditions.$dirty;
            const { showAllErrors } = this;

            return this.$v.$anyError && {
                nameRequired: nameDirty && !this.$v.name.required,
                nameMaxLength: nameDirty && !this.$v.name.maxLength,
                conditionsEmpty: conditionsDirty && !this.$v.conditions.required,
                conditions: Object.entries(this.$v.conditions.$each.$iter).map(([_, condition]) => {
                    const valueDirty = showAllErrors || condition.value.$dirty;
                    const customNameDirty = showAllErrors || condition.customName.$dirty;

                    return {
                        nameId: (showAllErrors || condition.nameId.$dirty) && !condition.nameId.required,
                        comparator: (showAllErrors || condition.comparator.$dirty)
                            && !condition.comparator.required,
                        value: valueDirty && !condition.value.required,
                        valueVersionFormat: valueDirty && !condition.value.versionFormat,
                        valueDateFormat: valueDirty && !condition.value.valueDate,
                        type: (showAllErrors || condition.type.$dirty) && !condition.type.required,
                        customName: customNameDirty && !condition.customName.required,
                        customNameMaxLength: customNameDirty && !condition.customName.maxLength,
                        age: valueDirty && !condition.value.age,
                        totalInAppPurchases: valueDirty && !condition.value.totalInAppPurchases,
                        levelId: valueDirty && !condition.value.levelId,
                        apiLevel: valueDirty && !condition.value.apiLevel
                    };
                })
            };
        }
    },
    async created() {
        this.setPage({
            pageTitle: 'Segment Settings'
        });
        try {
            const res = await monetizeAppService.getMonetizeApplications({
                filters: {
                    excludeArchive: true,
                    appKey: this.appKey
                },
                extraFields: ',currencyName,conversionRate,adUnits,virtualItem,hasAB,hasBiddingTest,hasQuickAB'
            });
            const { data: { applications } } = res;
            if (!applications?.length) {
                await this.goToSegmentsPage();
            }

            [this.selectedApp] = applications;

            const { appKey } = this.selectedApp;
            const [{ data: metadata }, countries] = await Promise.all([
                this.getWithCache({
                    api: segmentsApiKey,
                    req: `${segmentsApiPath}/metadata`,
                    ttl: false
                }),
                this.fetchCountries(),
                this.getPlacements(appKey),
                this.getActiveProvidersForApp(appKey),
                this.segmentId && this.getSegment(this.segmentId, this.appKey)
            ]);
            this.metadata = metadata;
            this.countries = countries.raw;
            this.supportedAdUnits = appToSupportedAdUnits(this.selectedApp, this.activatedProviders);
            this.loading = false;
        } catch (error) {
            this.loading = false;
            this.errorMessage = getHttpErrorMessage(error?.response?.data?.error);
        }
    },
    methods: {
        ...mapMutations([
            'setPage'
        ]),
        ...mapActions({
            getWithCache: 'cache/getWithCache',
            fetchCountries: 'countries/fetch'
        }),
        async getSegment(segmentId, appKey, refresh = true) {
            const response = await this.getWithCache({
                api: segmentsApiKey,
                req: `${segmentsApiPath}?appKey=${appKey}&testGroup=${this.testGroup}&segmentId=${segmentId}`,
                refresh
            });
            const [segment] = response.data ?? [];

            if (!segment) {
                await this.goToSegmentsPage();
                return;
            }

            this.name = segment.segmentName;
            this.conditions = segment.conditions;
            this.deliveryEnabled = segment.metaData.deliveryEnabled;
            if (segment.exceptions) {
                this.exceptions = segment.exceptions;
            }
        },
        async getPlacements(appKey, refresh = false) {
            const response = await this.getWithCache({
                api: placementsApiKey,
                req: `${placementsApiPath}&appKey=${appKey}&testGroup=${this.testGroup}`,
                refresh
            });
            const placementsData = filterArchivedPlacements(response.data);
            this.placements = Object.keys(placementsData).reduce((acc, adUnit) => ({
                ...acc,
                [adUnit]: placementsData[adUnit].map((placement) => ({
                    ...placement,
                    capping: placement.capping ? {
                        ...placement.capping,
                        unit: placement.capping.unit || 'd'
                    } : undefined
                }))
            }), {});
        },
        async getActiveProvidersForApp(appKey, refresh = false) {
            const response = await this.getWithCache({
                api: activatedProvidersForAppApiKey,
                req: `${activatedProvidersForAppApiPath}&appKey=${appKey}`,
                refresh
            });
            this.activatedProviders = response.data.activatedProviders;
        },
        async goToSegmentsPage({ successMessage } = {}) {
            const { appKey } = this.selectedApp;
            const query = successMessage ? { successMessage } : {};
            await this.$router.push({
                name: 'segmentsPage',
                query,
                params: { appKey }
            });
        },
        async saveSegment() {
            this.$v.$touch();
            if (this.$v.$anyError) {
                this.showAllErrors = true;
                this.$refs.editSegmentPageContainer.scrollTop = 0;
                return;
            }
            const originalExceptions = { ...this.exceptions };

            Object.keys(this.exceptions).forEach((adUnit) => {
                this.exceptions[adUnit] = this.exceptions[adUnit].map((exception) => {
                    const hasCappingFields = exception.capping && Object.keys(exception.capping).length > 0;
                    const hasPacingFields = exception.pacing && Object.keys(exception.pacing).length > 0;
                    let placementDeliveryEnabled;

                    if (exception.enabled !== undefined) {
                        placementDeliveryEnabled = exception.enabled ? undefined : false;
                    }

                    return {
                        ...exception,
                        enabled: placementDeliveryEnabled,
                        placementId: exception.placementId !== undefined ? exception.placementId.toString() : undefined,
                        capping: hasCappingFields ? {
                            ...exception.capping,
                            enabled: exception.capping.enabled ? 1 : 0
                        } : undefined,
                        pacing: hasPacingFields ? {
                            ...exception.pacing,
                            enabled: exception.pacing.enabled ? 1 : 0
                        } : undefined
                    };
                }).filter((ex) => Object.values(ex).filter((exception) => exception !== undefined).length > 1);
            });

            const segment = {
                segmentId: this.segmentId,
                name: this.name,
                metaData: {
                    deliveryEnabled: this.deliveryEnabled
                },
                conditions: this.conditions.map((condition) => ({
                    ...condition,
                    isCustom: condition.nameId === PROPERTY_IDS.customProperty
                })),
                exceptions: this.exceptions,
                testGroup: this.testGroup
            };

            try {
                this.loading = true;
                const update = !!segment.segmentId;
                const url = `${segmentsApiPath}?appKey=${this.selectedApp.appKey}`;
                const httpMethod = update ? http.put : http.post;

                await httpMethod(url, segment);
                await this.goToSegmentsPage({ successMessage: true });
            } catch (error) {
                this.exceptions = originalExceptions;
                this.loading = false;
                this.errorMessage = getHttpErrorMessage(error?.response?.data?.errorMessage);
            }
        },
        editReward({ adUnit, placement, exception }) {
            this.$modal.open({
                component: RewardModal,
                title: 'Edit Reward',
                props: {
                    adUnit,
                    placementId: placement.id,
                    placementName: placement.name,
                    virtualItemName: placement.virtualItemName,
                    defaultValue: Number(placement.virtualItemCount),
                    currentValue: exception.reward
                },
                onOK: (reward) => {
                    const wasReverted = reward === Number(placement.virtualItemCount);
                    const exceptions = this.exceptions[adUnit] ?? [];
                    const existingException = exceptions.find((ex) => ex.placementId === placement.id) || {};
                    const newException = clearUndefinedFields({
                        ...existingException,
                        placementId: placement.id,
                        reward: !wasReverted ? reward : undefined
                    });
                    this.exceptions = {
                        ...this.exceptions,
                        [adUnit]: [
                            ...exceptions.filter((ex) => ex !== existingException),
                            newException
                        ]
                    };
                }
            });
        },
        editCapping(props) {
            this.openCappingModal(props);
        },
        addCapping(props) {
            this.openCappingModal(props, true);
        },
        openCappingModal({ adUnit, placement, exception }, addMode = false) {
            this.$modal.open({
                component: CappingPacingModal,
                title: 'Edit Reward',
                props: {
                    adUnit,
                    placement,
                    placementId: placement.id,
                    placementName: placement.name,
                    forceAdDelivery: placement.isDefault,
                    adDelivery: addMode || (Boolean(exception.enabled ?? placement.delivery)),
                    defaultCapping: placement.capping,
                    currentCapping: exception.capping ?? placement.capping,
                    defaultPacing: placement.pacing,
                    currentPacing: exception.pacing ?? placement.pacing
                },
                onOK: ({
                    adDelivery, capping, pacing, reverted
                }) => {
                    const exceptions = this.exceptions[adUnit] ?? [];
                    const existingException = exceptions.find((ex) => ex.placementId === placement.id) || {};
                    const newException = clearUndefinedFields({
                        ...existingException,
                        placementId: placement.id,
                        enabled: !reverted ? adDelivery : undefined,
                        capping: !reverted ? capping : undefined,
                        pacing: !reverted ? pacing : undefined
                    });
                    this.exceptions = {
                        ...this.exceptions,
                        [adUnit]: [
                            ...exceptions.filter((ex) => ex !== existingException),
                            newException
                        ]
                    };
                }
            });
        },
        toggleExceptionActive({ adUnit, value }) {
            this.deliveryEnabled[adUnit] = value;
        },
        conditionTouched({ index, key }) {
            const condition = this.$v.conditions.$each.$iter[index];
            condition[key].$touch();
        }
    },
    validations: {
        name: {
            required,
            maxLength: maxLength(32)
        },
        conditions: {
            required,
            $each: {
                comparator: {
                    required: requiredIf((vm) => vm.nameId !== 1)
                },
                nameId: {
                    required
                },
                value: {
                    required,
                    versionFormat: (_, vm) => Boolean(
                        ![PROPERTY_IDS.sdkVersion, PROPERTY_IDS.osVersion].includes(vm.nameId)
                        || !vm.value || versionRegex.test(vm.value)
                    ),
                    valueDate: (_, vm) => Boolean(
                        ![PROPERTY_IDS.userCreationDate].includes(vm.nameId)
                        || !vm.value || moment(vm.value, 'YYYY-MM-DD', true).isValid()
                    ),
                    age: (_, vm) => Boolean(
                        PROPERTY_IDS.age !== vm.nameId
                        || !vm.value || (vm.value >= 1 && vm.value <= 99)
                    ),
                    totalInAppPurchases: (_, vm) => Boolean(
                        PROPERTY_IDS.totalInAppPurchases !== vm.nameId
                        || !vm.value || (vm.value >= minNumber && vm.value <= maxNumber)
                    ),
                    apiLevel: (_, vm) => Boolean(
                        PROPERTY_IDS.apiLevel !== vm.nameId
                        || !vm.value || (vm.value >= minNumber && vm.value <= maxNumber)
                    ),
                    levelId: (_, vm) => Boolean(
                        PROPERTY_IDS.levelId !== vm.nameId
                        || !vm.value || (vm.value >= minNumber && vm.value <= maxNumber)
                    )
                },
                type: {
                    required: requiredIf((vm) => vm.nameId === PROPERTY_IDS.customProperty)
                },
                customName: {
                    required: requiredIf((vm) => vm.nameId === PROPERTY_IDS.customProperty),
                    maxLength: (_, vm) => Boolean(vm.nameId !== PROPERTY_IDS.customProperty
                        || !vm.customName || vm.customName.length <= customNameMaxLength)
                }
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide";

.edit-segment-page {
    position: relative;
}

.edit-segment-page-content-container {
    width: 1020px;
    margin: 20px auto 0;
    background: $white;
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;
}

.edit-segment-page-inner-wrapper {
    background: $white;
    padding: 20px 20px 30px;
    margin-bottom: 21px;
    box-shadow: 2px 2px 2px 0 rgba(0, 0, 0, 0.1);
    border-top: $border;
}

.edit-segment-page-footer-buttons {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    & > div {
        margin-left: 16px;
    }
}

.link {
    color: $primary;
    text-decoration: none;
}

.segments-guide-link {
    margin-left: 4px;
    margin-right: 4px;
    font-weight: normal;
}
</style>
